import { Link } from "react-router-dom";
import { useSeo } from "@/utils/seo";
import { ArrowUpRight, BookOpen, FileDown, FileText, Terminal } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useLang } from "@/i18n";

const CATALOG_FLIP = "/pageflip";
const CATALOG_PDF = "/datenblaetter/katalog_ohne_preise.pdf";

export default function DownloadPage() {
    const { t } = useLang();
    useSeo(
        t("Downloads: Katalog, Software & Anleitungen | Müller", "Downloads: catalogue, software & manuals | Müller"),
        t(
            "Alle Downloads: Fachhandelskatalog als PDF und online blätterbar, Software für Kopfstationen und Bedienungsanleitungen zum Herunterladen.",
            "All downloads: trade catalogue as PDF and to browse online, software for headends and operating manuals to download."
        ),
    );
    return (
        <main data-testid="page-downloads" className="pt-28 sm:pt-32">
            <section className="py-10 sm:py-14">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                    <Reveal>
                        <Eyebrow>{t("Download", "Downloads")}</Eyebrow>
                        <h1 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900">
                            {t("Katalog, Software & Bedienungsanleitungen.", "Catalogue, software & operating manuals.")}
                        </h1>
                        <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl text-sm sm:text-base">
                            {t(
                                "Alles rund um unsere Produkte zum Herunterladen: der Fachhandelskatalog, Programmier-Software für Kopfstationen und Einzelumsetzer sowie alle Bedienungsanleitungen als PDF.",
                                "Everything about our products to download: the trade catalogue, programming software for headends and transmodulators, plus all operating manuals as PDFs."
                            )}
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="pb-16 sm:pb-24">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid md:grid-cols-3 gap-5">
                    <Reveal>
                        <div className="h-full flex flex-col rounded-2xl bg-white border border-slate-200 p-7 shadow-sm" data-testid="download-card-katalog">
                            <div className="flex items-start justify-between gap-3">
                                <span className="eyebrow text-[9.5px] px-2 py-1 rounded-sm border text-slate-600 bg-slate-100 border-slate-200">{t("KATALOG", "CATALOGUE")}</span>
                                <BookOpen className="h-5 w-5 shrink-0 text-red-700" />
                            </div>
                            <h2 className="mt-6 font-display text-xl font-bold text-slate-900">{t("Fachhandelskatalog", "Trade catalogue")}</h2>
                            <p className="mt-2 text-sm leading-relaxed text-slate-600 flex-1">
                                {t(
                                    "Der aktuelle Katalog ohne Preisangaben — direkt im Browser durchblättern oder als PDF herunterladen.",
                                    "The current catalogue without prices — browse right in your browser or download it as a PDF. (Content in German)"
                                )}
                            </p>
                            <div className="mt-5 flex flex-wrap gap-2">
                                <a
                                    href={CATALOG_FLIP}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="download-katalog-pageflip"
                                    className="inline-flex items-center gap-1.5 rounded-full bg-red-700 hover:bg-red-800 text-white text-xs font-semibold px-4 py-2.5 transition-colors"
                                >
                                    <BookOpen className="h-3.5 w-3.5" />
                                    {t("Pageflip öffnen", "Open flipbook")}
                                </a>
                                <a
                                    href={CATALOG_PDF}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="download-katalog-pdf"
                                    className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 hover:border-red-400 hover:text-red-700 text-slate-700 text-xs font-semibold px-4 py-2.5 transition-colors"
                                >
                                    <FileText className="h-3.5 w-3.5" />
                                    {t("PDF laden", "Download PDF")}
                                </a>
                            </div>
                        </div>
                    </Reveal>

                    <Reveal delay={0.08}>
                        <Link
                            to="/downloads/software"
                            data-testid="download-card-software"
                            className="group flex h-full flex-col rounded-2xl bg-white border border-slate-200 p-7 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
                        >
                            <div className="flex items-start justify-between gap-3">
                                <span className="eyebrow text-[9.5px] px-2 py-1 rounded-sm border text-slate-600 bg-slate-100 border-slate-200">{t("SOFTWARE", "SOFTWARE")}</span>
                                <Terminal className="h-5 w-5 shrink-0 text-red-700" />
                            </div>
                            <h2 className="mt-6 font-display text-xl font-bold text-slate-900">{t("Software", "Software")}</h2>
                            <p className="mt-2 text-sm leading-relaxed text-slate-600 flex-1">
                                {t(
                                    "Programmier-Software für die Kopfstationen DH8-Twin / DH8-Basis / DH4-Basis und alle Einzelumsetzer — dazu die aktuelle STRO-Firmware.",
                                    "Programming software for the DH8-Twin / DH8-Basis / DH4-Basis headends and all transmodulators — plus the latest STRO firmware."
                                )}
                            </p>
                            <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-red-700">
                                {t("Zur Software", "To the software")}
                                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </span>
                        </Link>
                    </Reveal>

                    <Reveal delay={0.16}>
                        <Link
                            to="/downloads/bedienungsanleitungen"
                            data-testid="download-card-anleitungen"
                            className="group flex h-full flex-col rounded-2xl bg-white border border-slate-200 p-7 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
                        >
                            <div className="flex items-start justify-between gap-3">
                                <span className="eyebrow text-[9.5px] px-2 py-1 rounded-sm border text-slate-600 bg-slate-100 border-slate-200">{t("ANLEITUNGEN", "MANUALS")}</span>
                                <FileDown className="h-5 w-5 shrink-0 text-red-700" />
                            </div>
                            <h2 className="mt-6 font-display text-xl font-bold text-slate-900">{t("Bedienungsanleitungen", "Operating manuals")}</h2>
                            <p className="mt-2 text-sm leading-relaxed text-slate-600 flex-1">
                                {t(
                                    "Alle Bedienungsanleitungen als PDF — sortiert nach Kopfstationstechnik, Einzelumsetzern, Einkabelsystem, Antennenheizung und Infosystem.",
                                    "All operating manuals as PDFs — sorted by headend technology, transmodulators, single-cable systems, antenna heating and info systems."
                                )}
                            </p>
                            <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-red-700">
                                {t("Zu den Anleitungen", "To the manuals")}
                                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                            </span>
                        </Link>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
